import {CardContent, CardHeader, FormHelperText, Grid} from "@mui/material";
import {useLocalization, useWordingFlavor} from "@smalox/shared";
import {
	BreadcrumbItem,
	BreadcrumbLink,
	Breadcrumbs,
	Checkbox,
	EditForm,
	Now,
	PlainDateTime,
	PlainDateTimePicker,
} from "@variocube/app-ui";
import React, {Fragment, useState} from "react";
import {useAsync} from "react-async-hook";
import {Link, useNavigate} from "react-router-dom";
import {
	Compartment,
	LocationTree,
	TenantUserSummary,
	useBookingApi,
	useCompartmentApi,
	useLocationApi,
	useTenantUserApi,
} from "../../api";
import {useAnyPermission, useLocationPermission} from "../../tenant";
import {UserTimeZoneId} from "../../utils/date";
import {useSearchParam} from "../../utils/search";
import {CompartmentSelect} from "../compartment/CompartmentSelect";
import {LocationSelect} from "../location/LocationSelect";
import {UserSelect} from "../user/UserSelect";
import {View, ViewHeader} from "../View";

export function BookingCreate() {
	const {t} = useLocalization();
	const wordingFlavor = useWordingFlavor();
	const navigate = useNavigate();
	const [userParam] = useSearchParam("user", "");
	const [compartmentParam] = useSearchParam("compartment", "");

	const [location, setLocation] = useState<LocationTree | null>(null);
	const [compartment, setCompartment] = useState<Compartment | null>(null);
	const [users, setUsers] = useState<TenantUserSummary[]>([]);
	const [from, setFrom] = useState<PlainDateTime | null>(Now.plainDateTimeISO());
	const [until, setUntil] = useState<PlainDateTime | null>(null);
	const [selfManaged, setSelfManaged] = useState(false);
	const [temporary, setTemporary] = useState(false);

	const {createBooking} = useBookingApi();
	const {getUser} = useTenantUserApi();
	const {getCompartment} = useCompartmentApi();
	const {getLocationSettings} = useLocationApi();

	const canCreateBooking = useLocationPermission("BOOKING_CREATE", compartment?.location.id);
	const canReadUsers = useAnyPermission("TENANT_USER_READ");

	const loadUser = useAsync(async (username?: string) => {
		if (username) {
			setUsers([await getUser(username)]);
		}
	}, [userParam]);

	const loadCompartment = useAsync(async (compartmentId?: string) => {
		if (compartmentId) {
			setCompartment(await getCompartment(Number(compartmentId)));
		}
	}, [compartmentParam]);

	useAsync(async (locationId?: number) => {
		if (locationId) {
			const locationSettings = await getLocationSettings(locationId);
			setTemporary(locationSettings.booking.temporary ?? false);
		}
	}, [compartment?.location.id]);

	async function handleSave() {
		if (!compartment) {
			throw new Error(t(`bookings.create.errorNoCompartment.${wordingFlavor}`));
		}

		if (!from) {
			throw new Error(t(`bookings.create.errorNoFrom.${wordingFlavor}`));
		}

		const createdBooking = await createBooking({
			compartmentId: compartment.id,
			users: users.map(user => user.user.username),
			from: from.toZonedDateTime(UserTimeZoneId).toInstant().toString(),
			until: until?.toZonedDateTime(UserTimeZoneId).toInstant().toString(),
			temporary,
			selfManaged,
		});
		console.log("Created", createdBooking);
		navigate(`../${createdBooking.id}`);
	}

	return (
		<View>
			<Breadcrumbs>
				<BreadcrumbLink component={Link} to={".."}>{t(`bookings.title.${wordingFlavor}`)}</BreadcrumbLink>
				<BreadcrumbItem>{t(`bookings.create.title.${wordingFlavor}`)}</BreadcrumbItem>
			</Breadcrumbs>
			<ViewHeader title={t(`bookings.create.title.${wordingFlavor}`)} />
			<EditForm
				loading={loadUser.loading || loadCompartment.loading}
				onSave={handleSave}
				labels={t}
			>
				<CardHeader title={t(`bookings.compartment.${wordingFlavor}`)} />
				<CardContent>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6} md={4}>
							<LocationSelect
								value={location}
								onChange={setLocation}
								fullWidth
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={4}>
							<CompartmentSelect
								value={compartment}
								onChange={setCompartment}
								status="AVAILABLE"
								location={location}
								required
							/>
						</Grid>
					</Grid>
				</CardContent>
				{canReadUsers && (
					<Fragment>
						<CardHeader title={t("bookings.users.title")} />
						<CardContent>
							<Grid container spacing={2}>
								<Grid item xs={12} md={8}>
									<UserSelect
										value={users}
										onChange={setUsers}
										required
									/>
								</Grid>
							</Grid>
						</CardContent>
					</Fragment>
				)}
				{canCreateBooking && (
					<CardContent>
						<Checkbox
							label={t("bookings.selfManaged.title")}
							checked={selfManaged}
							onChange={setSelfManaged}
						/>
						<FormHelperText>{t(`bookings.selfManaged.helperText.${wordingFlavor}`)}</FormHelperText>
					</CardContent>
				)}
				<CardHeader title={t("bookings.timeframe")} />
				<CardContent>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6} md={4}>
							<PlainDateTimePicker
								label={t("bookings.from")}
								value={from}
								onChange={setFrom}
								fullWidth
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={4}>
							<PlainDateTimePicker
								label={t("bookings.until")}
								value={until}
								onChange={setUntil}
								fullWidth
							/>
						</Grid>
					</Grid>
				</CardContent>
				<CardContent>
					<Checkbox label={t("bookings.temporary.title")} checked={temporary} onChange={setTemporary} />
					<FormHelperText>{t(`bookings.temporary.helperText.${wordingFlavor}`)}</FormHelperText>
				</CardContent>
			</EditForm>
		</View>
	);
}
